import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConversationAssignee,
  ConversationData,
  ConversationStatusAssigned,
  ConversationStatusGroup,
  InternalNoteLastKey,
  WaConfigData,
} from './model';
import { RootState } from '../root';
import { ChannelDetails } from '../channel';

export const CONVERSATION_FETAURE_KEY = 'conversations';

export interface ConversationSliceState {
  conversationStatusMap: Record<
    ConversationStatusGroup | ConversationStatusAssigned,
    string[]
  >;
  currentConversation: ConversationData | null;
  channel: ChannelDetails;
  waConfig: WaConfigData[];
  alreadyAssignedMembers: ConversationAssignee[];
  notesPaginationKey: { [channel: string]: InternalNoteLastKey };
  conversationChannelId: string;
}

export const initialConversationDataState: ConversationSliceState = {
  conversationStatusMap: {
    [ConversationStatusGroup.OPEN]: [],
    [ConversationStatusAssigned.ASSIGNED]: [],
    [ConversationStatusGroup.INPROGRESS]: [],
    [ConversationStatusGroup.CLOSED]: [],
  },
  currentConversation: null,
  channel: {} as ChannelDetails,
  waConfig: [] as WaConfigData[],
  alreadyAssignedMembers: [],
  notesPaginationKey: {},
  conversationChannelId: '',
};

export const conversationSlice = createSlice({
  name: CONVERSATION_FETAURE_KEY,
  initialState: initialConversationDataState,
  reducers: {
    loadConversationStatusMap(
      state,
      action: PayloadAction<
        Record<ConversationStatusGroup | ConversationStatusAssigned, string[]>
      >
    ) {
      state.conversationStatusMap = action.payload;
    },
    updateCurrentConversation(
      state,
      action: PayloadAction<ConversationData | null>
    ) {
      state.currentConversation = action.payload;
    },
    updateChannel(state, action: PayloadAction<ChannelDetails>) {
      state.channel = action.payload;
    },
    updateWaConfig(state, action: PayloadAction<WaConfigData[]>) {
      state.waConfig = action.payload;
    },
    updateAlreadyAssignedMembers(
      state,
      action: PayloadAction<ConversationAssignee[]>
    ) {
      state.alreadyAssignedMembers = action.payload;
    },
    updateInternalNotesPagination(
      state,
      action: PayloadAction<{
        channelId: string;
        pagination: InternalNoteLastKey;
      }>
    ) {
      const { channelId, pagination } = action.payload;
      state.notesPaginationKey[channelId] = pagination;
    },
    updateConversationChannelId(state, action: PayloadAction<string>) {
      state.conversationChannelId = action.payload;
    },
  },
});

export const {
  loadConversationStatusMap,
  updateCurrentConversation,
  updateChannel,
  updateWaConfig,
  updateAlreadyAssignedMembers,
  updateInternalNotesPagination,
  updateConversationChannelId,
} = conversationSlice.actions;

export const getConversationSliceState = (
  rootState: RootState
): ConversationSliceState => rootState[CONVERSATION_FETAURE_KEY];

export const getConversationStatusMap = createSelector(
  getConversationSliceState,
  (state) => state.conversationStatusMap
);

export const getCurrentConversation = createSelector(
  getConversationSliceState,
  (state) => state.currentConversation
);

export const getConversationChannel = createSelector(
  getConversationSliceState,
  (state) => state.channel
);

export const getWaConfig = createSelector(getConversationSliceState, (state) =>
  state.waConfig?.find((x) => x.isDefault)
);

export const getAssignedMembers = createSelector(
  getConversationSliceState,
  (state) => state.alreadyAssignedMembers
);

export const getCurrentChannelInternalNotePaginationKey = (channelId: string) =>
  createSelector(
    getConversationSliceState,
    (state) => state.notesPaginationKey?.[channelId] || false
  );

export const getConversationChannelId = createSelector(
  getConversationSliceState,
  (state) => state.conversationChannelId
);
